import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import Navbar from "@/components/Navbar";

const SCHEMA = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "About Lakshmi — AI Social Media Buddy for India",
    "description": "Lakshmi is an AI-powered personal brand tool built for Indian professionals. Our mission: make consistent, high-quality social media content accessible to every professional in India.",
    "url": "https://hilakshmi.com/about",
    "publisher": {
        "@type": "Organization",
        "name": "Lakshmi",
        "url": "https://hilakshmi.com",
        "foundingLocation": { "@type": "Place", "name": "India" }
    }
};

export default function About() {
    useEffect(() => {
        const s = document.createElement("script");
        s.type = "application/ld+json";
        s.text = JSON.stringify(SCHEMA);
        document.head.appendChild(s);
        document.title = "About Lakshmi — AI Personal Brand Tool Built for India";
        return () => s.remove();
    }, []);

    return (
        <div className="min-h-screen">
            <Navbar />
            <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
                <div className="flex flex-col lg:flex-row items-start gap-12 mb-16">
                    {/* Text side */}
                    <div className="flex-1 max-w-2xl">
                        <div className="lk-label mb-3">Our story</div>
                        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight mb-8">
                            Built for Indian professionals <span className="lk-gradient-text">who have something to say</span>
                        </h1>

                <div className="space-y-6 text-[var(--lk-muted)] leading-relaxed">
                    <p>
                        Every professional in India has insights worth sharing. The engineer who shipped a product that scaled to 10 million users. The founder who pivoted three times before finding product-market fit. The sales leader who cracked enterprise deals. The consultant who transformed how a team thinks about strategy.
                    </p>
                    <p>
                        Most of them never post. Not because they have nothing to say — but because translating lived experience into content that resonates on LinkedIn, X, Instagram or Reddit is a skill that takes years to develop, and hours they don't have.
                    </p>
                    <p className="text-[var(--lk-text)]">
                        Lakshmi exists to close that gap.
                    </p>
                    <p>
                        We built an AI agent — not just an AI tool — that acts as your social media buddy. Drop a rough idea. Lakshmi's four-agent pipeline (Content Strategist, Voice Coach, Ghostwriter, QA Editor) turns it into a platform-native post in your voice, in under 60 seconds. Multi-platform: LinkedIn, X, Instagram, Reddit.
                    </p>
                    <p>
                        We're India-first. Our early users are from Bengaluru, Mumbai, Delhi, Hyderabad, and Pune. We understand the Indian professional context — the industries, the career trajectories, the kind of content that resonates with an Indian LinkedIn audience.
                    </p>
                    <p>
                        Lakshmi is in beta. We're building fast, listening hard, and shipping improvements every week. If you're an Indian professional who wants to build your personal brand — <Link to="/signup" className="text-[var(--lk-primary)] hover:underline">join us</Link>.
                    </p>
                </div>

                <div className="mt-12 flex items-center gap-3">
                    <Sparkles className="w-5 h-5 text-[var(--lk-primary)]" />
                    <span className="font-semibold">Lakshmi</span>
                    <span className="text-[var(--lk-muted)] text-sm">— Your personal brand, automated.</span>
                </div>

                <div className="mt-10">
                    <Link to="/signup" className="lk-btn-primary rounded-xl px-7 py-3.5 inline-flex items-center gap-2 text-sm">
                        Start building your brand — free <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
                    </div>
                    {/* Character image */}
                    <div className="flex-shrink-0 flex flex-col items-center gap-6">
                        <picture>
                            <source srcSet="/image/lakshmi_nobg.webp" type="image/webp" />
                            <img src="/image/lakshmi_nobg_opt.png" alt="Lakshmi — AI personal brand buddy"
                                className="w-48 sm:w-64 lk-float" loading="lazy"
                                style={{ filter: "drop-shadow(0 0 30px rgba(124,58,237,0.4))" }} />
                        </picture>
                        {/* Mission card */}
                        <div className="lk-border lk-surface rounded-2xl p-5 max-w-xs text-center">
                            <div className="text-xs text-[var(--lk-primary)] font-semibold uppercase tracking-wider mb-2">Our mission</div>
                            <p className="text-sm text-[var(--lk-muted)] leading-relaxed">
                                Make consistent, high-quality social media content accessible to every professional in India — not just those who can afford agencies.
                            </p>
                        </div>
                        {/* India-first stats */}
                        <div className="flex gap-3 flex-wrap justify-center">
                            {[{ n: "5+", l: "Indian cities" }, { n: "4", l: "Platforms" }, { n: "Free", l: "to start" }].map(s => (
                                <div key={s.n} className="lk-border lk-surface rounded-xl px-4 py-3 text-center min-w-[80px]">
                                    <div className="font-bold lk-gradient-text text-lg">{s.n}</div>
                                    <div className="text-xs text-[var(--lk-muted)]">{s.l}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
